import { Injectable } from "@angular/core";
import {
	AccessNotice,
	AccountSwitchRequest,
	ACCOUNT_SWITCH_STORAGE_KEY,
	ACCESS_ERROR_STORAGE_KEY,
} from "../data/constants";
import { PendingRedirect } from "./auth.models";

@Injectable({ providedIn: "root" })
export class AuthStateStorageService {
	private readonly pendingRedirectKey =
		"core-automatizaciones-auth-redirect";
	private readonly failedRedirectKey =
		"core-automatizaciones-auth-redirect-failed";
	private readonly localLogoutKey =
		"core-automatizaciones-local-logout";
	private readonly stateLifetimeMs = 5 * 60 * 1000;

	getAccessNotice(): AccessNotice | null {
		const value = sessionStorage.getItem(ACCESS_ERROR_STORAGE_KEY);
		if (!value) {
			return null;
		}

		try {
			const notice = JSON.parse(value) as AccessNotice;
			return notice?.type && notice?.message
				? notice
				: { type: "unexpected", message: value };
		} catch {
			return { type: "unexpected", message: value };
		}
	}

	setAccessNotice(notice: AccessNotice): void {
		sessionStorage.setItem(
			ACCESS_ERROR_STORAGE_KEY,
			JSON.stringify(notice)
		);
	}

	clearAccessNotice(): void {
		sessionStorage.removeItem(ACCESS_ERROR_STORAGE_KEY);
	}

	getAccountSwitch(): AccountSwitchRequest | null {
		const value = sessionStorage.getItem(ACCOUNT_SWITCH_STORAGE_KEY);
		if (!value) {
			return null;
		}

		try {
			const request = JSON.parse(value) as AccountSwitchRequest;
			return request?.currentEmail &&
				request?.requestedEmail &&
				request?.returnUrl
				? request
				: null;
		} catch {
			return null;
		}
	}

	setAccountSwitch(request: AccountSwitchRequest): void {
		sessionStorage.setItem(
			ACCOUNT_SWITCH_STORAGE_KEY,
			JSON.stringify(request)
		);
	}

	clearAccountSwitch(): void {
		sessionStorage.removeItem(ACCOUNT_SWITCH_STORAGE_KEY);
	}

	getPendingRedirect(): PendingRedirect | null {
		const rawValue = sessionStorage.getItem(this.pendingRedirectKey);
		if (!rawValue) {
			return null;
		}

		try {
			const value = JSON.parse(rawValue) as PendingRedirect;
			if (
				!value.createdAt ||
				Date.now() - value.createdAt > this.stateLifetimeMs
			) {
				this.clearPendingRedirect();
				return null;
			}
			return value;
		} catch {
			this.clearPendingRedirect();
			return null;
		}
	}

	setPendingRedirect(value: PendingRedirect): void {
		sessionStorage.setItem(
			this.pendingRedirectKey,
			JSON.stringify(value)
		);
	}

	clearPendingRedirect(): void {
		sessionStorage.removeItem(this.pendingRedirectKey);
	}

	hasFailedRedirect(): boolean {
		const createdAt = Number(
			sessionStorage.getItem(this.failedRedirectKey) || 0
		);

		if (
			!createdAt ||
			Date.now() - createdAt > this.stateLifetimeMs
		) {
			this.clearFailedRedirect();
			return false;
		}

		return true;
	}

	setFailedRedirect(): void {
		sessionStorage.setItem(
			this.failedRedirectKey,
			Date.now().toString()
		);
	}

	clearFailedRedirect(): void {
		sessionStorage.removeItem(this.failedRedirectKey);
	}

	markLocalLogout(): void {
		localStorage.setItem(this.localLogoutKey, Date.now().toString());
	}

	consumeLocalLogout(): boolean {
		const createdAt = Number(localStorage.getItem(this.localLogoutKey) || 0);
		localStorage.removeItem(this.localLogoutKey);

		return Boolean(
			createdAt &&
				Date.now() - createdAt <= this.stateLifetimeMs
		);
	}
}
